import React from 'react';
import Skeleton from 'react-loading-skeleton';
import { capitalize } from 'lodash';
import { IApiQueryOptions } from 'api/ApiClient';
import { Cell, Empty, HeaderCell, HeaderRow, Row, SortLabel, Table, TableBody, TableHead, TableTimestamp, TooltipIcon } from 'components';
import { theme } from 'design';
import { ILedgerEntry } from 'types';
import { centsToDollarsDisplay, formatDateForUI, getTimeWithZone } from 'utils';
import { DEFAULT_PAGE_SIZE, isSortDesc } from 'utils/table';

interface ILedgerRowProps {
  isLoading?: boolean;
  data?: ILedgerEntry;
}

export const LedgerRow = ({ data, isLoading, ...rest }: ILedgerRowProps) => {
  const { id, createdAt, entryType, description, txnRefNotes, txnRefId, amount, balance } = data ?? {};
  return (
    <Row {...rest}>
      <Cell data-cy="timestamp">{createdAt && <TableTimestamp align="left" date={formatDateForUI(createdAt)} time={getTimeWithZone(createdAt)} isLoading={isLoading} />}</Cell>

      <Cell minWidth={150} data-cy="type">
        {isLoading ? <Skeleton width={150} /> : entryType ? capitalize(entryType.replaceAll('_', ' ')) : ''}
      </Cell>

      <Cell minWidth={300} maxWidth={350} wrapText data-cy="description">
        {isLoading ? (
          <Skeleton width={300} />
        ) : (
          <>
            {description || '--'}
            {txnRefNotes && (
              <TooltipIcon id={id} iconSize={13.25} iconMarginLeft={theme.space.xs} place="top">
                {txnRefNotes}
              </TooltipIcon>
            )}
          </>
        )}
      </Cell>

      <Cell align="left" data-cy="employee-name">
        {isLoading ? <Skeleton width={55} /> : data?.employee?.name || '--'}
      </Cell>

      <Cell align="left" data-cy="related-id">
        {isLoading ? <Skeleton width={55} /> : txnRefId || '--'}
      </Cell>

      <Cell minWidth={100} align="right" data-cy="amount">
        <strong>{isLoading ? <Skeleton width={100} /> : centsToDollarsDisplay(entryType === 'debit' ? -amount : amount)}</strong>
      </Cell>

      <Cell minWidth={100} align="right" data-cy="new-balance">
        <strong>{isLoading ? <Skeleton width={100} /> : centsToDollarsDisplay(balance)}</strong>
      </Cell>
    </Row>
  );
};

interface ILedgerTableProps {
  isLoading?: boolean;
  ledgerEntries: ILedgerEntry[];
  urlQuery: IApiQueryOptions;
  onSortChange: (sort: string) => void;
}

const LedgerTable: React.FC<ILedgerTableProps> = ({ isLoading, ledgerEntries, urlQuery, onSortChange }) => {
  const currentSort = urlQuery.sort || '';
  const isDescendingSort = isSortDesc(currentSort);

  return (
    <Table sticky scrollable data-cy="reports-ledger-table">
      <TableHead>
        <HeaderRow sticky>
          <HeaderCell>
            <SortLabel data-cy="date-header" label="Date" isSorted isSortedDesc={isDescendingSort} onClick={() => onSortChange('created_at')} isLoading={isLoading} />
          </HeaderCell>
          <HeaderCell data-cy="type-header">{!isLoading && 'Type'}</HeaderCell>
          <HeaderCell data-cy="description-header">{!isLoading && 'Description'}</HeaderCell>
          <HeaderCell align="left" data-cy="employee-header">
            {!isLoading && 'Employee'}
          </HeaderCell>
          <HeaderCell align="left" data-cy="transaction-id-header">
            {!isLoading && 'Transaction ID'}
          </HeaderCell>
          <HeaderCell align="right" data-cy="amount-header">
            {!isLoading && 'Amount'}
          </HeaderCell>
          <HeaderCell align="right" data-cy="new-balance-header">
            {!isLoading && 'New Balance'}
          </HeaderCell>
        </HeaderRow>
      </TableHead>

      <TableBody>
        {isLoading ? (
          [...Array(DEFAULT_PAGE_SIZE)].map((_, i) => <LedgerRow key={`loading-row-${i}`} isLoading data={undefined} data-cy="loading-row" />)
        ) : ledgerEntries?.length === 0 ? (
          <Row>
            <Cell colSpan={8}>
              <Empty container containerHeight={300} icon="Payment" message="No results. Try a different date range." />
            </Cell>
          </Row>
        ) : (
          ledgerEntries?.map((datum: ILedgerEntry) => <LedgerRow key={`ledger-entry-${datum.id}`} data={datum} data-cy="ledger-row" data-value={datum.id} />)
        )}
      </TableBody>
    </Table>
  );
};

export default LedgerTable;
